import { EventState, EventStateType } from '@bp/planung-graphql-types';
import { TimetableViewEntryType } from '@bp/ui-components';
import { SubstitutionFormRowStatus } from '../../../../components/Substitutions/forms/SubstitutionFormRow';
import { SubstitutionReason } from '../../../../types/planung-graphql-client-defs';

export type SubstitutionDataEntry = {
  uuid: string;
  name: string;
  shortName: string;
};

// TimetableViewEntryType hatte bis @bp/ui-components v13 ein freies `additionalData?: unknown`.
// Ab v14 entfernt – die Raum-Daten je Eintrag tragen wir jetzt typisiert selbst.
export type SubstitutionTimetableEntry = TimetableViewEntryType & {
  rooms: SubstitutionDataEntry[];
};

export type SubstitutionDataTeacherEntry = SubstitutionDataEntry & {
  status: Extract<SubstitutionFormRowStatus, 'cancellation-school-event' | 'normal'>;
  givenSubstituteHours: number | null;
};

export type SubstitutionDataMissingTeacherEntry = SubstitutionDataEntry & {
  status: Extract<SubstitutionFormRowStatus, 'on-school-event' | 'signed-out'>;
  fullDay: boolean;
  state?: EventState;
};

export type SubstitutionDataSubstitutionTeacherEntry = SubstitutionDataEntry & {
  status: Extract<SubstitutionFormRowStatus, 'substitution'>;
  givenSubstituteHours: number | null;
};

export const createMapFromArray = <T extends { uuid: string }>(array: T[]) => {
  return new Map<string, T>(array.map((item) => [item.uuid, item]));
};

const intervalsOverlap = (a: { start: Date; end: Date }, b: { start: Date; end: Date }) =>
  a.start.getTime() < b.end.getTime() && b.start.getTime() < a.end.getTime();

/**
 * Berechnet für zeitlich überlappende Einträge eine `group`-Spalte (index/length), damit
 * `TimetableView` sie nebeneinander statt übereinander darstellt – egal ob die Überlappung von
 * einer echten Teilung stammt oder ein Konflikt ist (z.B. Klasse doppelt eingeplant).
 *
 * Transitiv überlappende Einträge bilden einen Cluster und bekommen gemeinsam dieselbe
 * Spaltenzahl (`length`), damit die Karten im Cluster gleich breit sind, statt springend
 * unterschiedlich breit zu werden.
 */
export const assignOverlapGroups = <T extends { start: Date; end: Date }>(
  entries: T[],
): (T & {
  group: { index: number; length: number };
})[] => {
  if (entries.length === 0) return [];

  const clusterOf = new Map<number, number>();
  let nextClusterId = 0;

  entries.forEach((_entry, originalIndex) => {
    if (clusterOf.has(originalIndex)) return;

    const clusterId = nextClusterId++;
    const queue = [originalIndex];
    clusterOf.set(originalIndex, clusterId);

    while (queue.length > 0) {
      const current = queue.pop();
      if (current === undefined) continue;
      entries.forEach((entry, otherIndex) => {
        if (clusterOf.has(otherIndex)) return;
        if (intervalsOverlap(entries[current], entry)) {
          clusterOf.set(otherIndex, clusterId);
          queue.push(otherIndex);
        }
      });
    }
  });

  const clusterMembers = new Map<number, number[]>();
  clusterOf.forEach((clusterId, originalIndex) => {
    const members = clusterMembers.get(clusterId) ?? [];
    members.push(originalIndex);
    clusterMembers.set(clusterId, members);
  });

  const columnByEntry = new Map<number, number>();
  const columnCountByCluster = new Map<number, number>();

  clusterMembers.forEach((members, clusterId) => {
    // Deterministische Sweep-Reihenfolge: nach Start, bei Gleichstand längere Karte zuerst.
    const ordered = [...members].sort((a, b) => {
      const startDiff = entries[a].start.getTime() - entries[b].start.getTime();
      if (startDiff !== 0) return startDiff;
      const endDiff = entries[b].end.getTime() - entries[a].end.getTime();
      if (endDiff !== 0) return endDiff;
      return a - b;
    });

    // Jede Karte bekommt die kleinste Spalte, deren vorherige Karte schon beendet ist.
    const columnEndTimes: number[] = [];
    ordered.forEach((originalIndex) => {
      const entry = entries[originalIndex];
      let column = columnEndTimes.findIndex((end) => end <= entry.start.getTime());
      if (column === -1) {
        column = columnEndTimes.length;
        columnEndTimes.push(entry.end.getTime());
      } else {
        columnEndTimes[column] = entry.end.getTime();
      }
      columnByEntry.set(originalIndex, column);
    });

    columnCountByCluster.set(clusterId, columnEndTimes.length);
  });

  return entries.map((entry, originalIndex) => ({
    ...entry,
    group: {
      index: columnByEntry.get(originalIndex) ?? 0,
      length: columnCountByCluster.get(clusterOf.get(originalIndex) ?? 0) ?? 1,
    },
  }));
};

type SubstitutionRelevanceClass = SubstitutionDataEntry & { relevantForSubstitution?: boolean | null };

/**
 * Findet Klassen, die im aktiven Stundenplan des gewählten Tages hängen, für die aber wegen eines
 * fehlenden `relevantForSubstitution` gar keine Termine entstehen können.
 *
 * Hintergrund: `relevantForEventsClasses()` im Backend matcht mit
 * `WHERE c.relevantForSubstitution = true`. Steht das Flag auf false oder null, verwirft
 * `createEventsMapFromTimetableCardData` die Klasse; bleibt keine übrig, entsteht überhaupt kein
 * Event. Im Vertretungsplan fehlt die Klasse dann einfach, ohne jeden Hinweis auf die Ursache.
 *
 * Bewusst wird nur das Flag geprüft und nicht "Klasse hat heute keine Termine": die einzige im
 * Frontend verfügbare Referenzmenge wäre `TimetableVersion.classes`, und das ist die
 * HAS_TIMETABLE_CONFIG-Liste, in der (spätestens seit Migration 2026-07-10_001) jede Klasse des
 * Schuljahres hängt — auch ohne eine einzige platzierte Karte. Auf Staging wichen dort 7 von 130
 * Organisationen ab, eine davon um 13 Klassen. Das Flag ist die Ursache und ist eindeutig.
 *
 * Gemeldet wird nur, wenn mindestens eine Klasse Termine hat: an Ferien- und Feiertagen hat keine
 * welche, dort wäre der Hinweis bloß Rauschen.
 */
export const findClassesWithoutEvents = ({
  timetableVersions,
  events,
  classMap,
}: {
  timetableVersions: { classes: { uuid: string }[] }[];
  events: { involvedClasses: { uuid: string }[] }[];
  classMap: Map<string, SubstitutionRelevanceClass>;
}): SubstitutionDataEntry[] => {
  const classesWithEvents = new Set(events.flatMap((e) => e.involvedClasses.map((c) => c.uuid)));

  if (classesWithEvents.size === 0) {
    return [];
  }

  const classUuidsInActivePlan = new Set(timetableVersions.flatMap((v) => v.classes.map((c) => c.uuid)));

  return [...classUuidsInActivePlan]
    .map((uuid) => classMap.get(uuid))
    .filter((c): c is SubstitutionRelevanceClass => c !== undefined && c.relevantForSubstitution !== true)
    .map((c) => ({ uuid: c.uuid, name: c.name, shortName: c.shortName }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
};

export const mapTeachers = (
  persons: { uuid: string; givenSubstituteHours?: number | null }[],
  teacherMap: Map<string, { uuid: string; displayName: string; shortName: string }>,
  hasClassesOrGroups: boolean,
): SubstitutionDataTeacherEntry[] => {
  return persons
    .map((person): SubstitutionDataTeacherEntry => {
      const teacher = teacherMap.get(person.uuid);
      return {
        uuid: teacher?.uuid ?? '',
        name: teacher?.displayName ?? '',
        shortName: teacher?.shortName ?? '',
        status: hasClassesOrGroups ? 'cancellation-school-event' : 'normal',
        givenSubstituteHours: person.givenSubstituteHours ?? null,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const mapSubstitutionTeachers = (
  persons: { uuid: string; isCoveringTeacher: boolean; givenSubstituteHours?: number | null }[],
  teacherMap: Map<string, any>,
): SubstitutionDataSubstitutionTeacherEntry[] => {
  return persons
    .filter((person) => person.isCoveringTeacher)
    .map((person) => {
      const teacher = teacherMap.get(person.uuid);
      return {
        uuid: teacher?.uuid ?? '',
        name: teacher?.displayName ?? '',
        shortName: teacher?.shortName ?? '',
        status: 'substitution',
        givenSubstituteHours: person.givenSubstituteHours ?? null,
      };
    });
};

export const parseState = (state?: string | null): Partial<EventStateType>[] => {
  if (!state) {
    return [];
  }
  try {
    const parsedState = JSON.parse(state);
    if (typeof parsedState === 'object' && parsedState !== null) {
      return parsedState as Partial<EventStateType>[];
    }
    console.warn('Parsed state is not an object:', parsedState);
    return [];
  } catch (error) {
    console.error('Failed to parse state:', error);
    return [];
  }
};

/**
 * Die Stunden für das Zusammenlegen: alle nicht ausgefallenen und nicht weggelegten Stunden der Klassen, an denen die
 * gewählte Stunde beteiligt ist, die gewählte Stunde eingeschlossen. Hängt bewusst nicht am Klassen-Stundenplan
 * rechts – dessen Auswahl kann zu einer anderen Zeile gehören.
 */
export const findMergeTimetableEvents = <
  T extends { uuid: string; involvedClasses: { uuid: string }[]; substitutionReason?: SubstitutionReason | null },
>(
  events: T[],
  selected: T,
): T[] => {
  const classUuids = new Set(selected.involvedClasses.map((c) => c.uuid));
  return events.filter(
    (event) =>
      (event.uuid === selected.uuid || event.involvedClasses.some((c) => classUuids.has(c.uuid))) &&
      event.substitutionReason !== SubstitutionReason.Cancelled &&
      event.substitutionReason !== SubstitutionReason.Aggregated,
  );
};
